import { useEffect } from 'react';

import {
  abTest as smokeAbTest,
  TEST_CONFIG as SMOKE_TEST_CONFIG,
} from 'js/ab_tests/tests/autotest_login_modal_2026_q3_v1_smoke';
import {
  abTest as waveAbTest,
  TEST_CONFIG as WAVE_TEST_CONFIG,
} from 'js/ab_tests/tests/autotest_login_modal_2026_q3_v1';

import type { LoginModalEventDetail } from '../LoginModalManager';
import { LoginModalAutotestHooks, PartialConfigurableText } from '../types';
import { ARMS, ArmSpec, Bucket, OpenContext } from './armRegistry';
import { rehydrateDockPill } from './arms/dismissal_dock';
import { pushTrailEntry } from './memory';
import { AutotestStamp, stampTrackingContext } from './tracking';
import { getTrailSeed } from './workContext';

export const WAVE_TEST_NAME = 'autotest_login_modal_2026_q3_v1';
export const SMOKE_TEST_NAME = 'autotest_login_modal_2026_q3_v1_smoke';

export type LoginModalAutotestResolution = {
  detail: LoginModalEventDetail;
  autotest?: LoginModalAutotestHooks;
};

type ResolvedTest = {
  testName: string;
  active: boolean;
  bucket: Bucket;
};

// Neither yml declares a holdout, so 'holdout' is unreachable; the framework
// return type still includes it, and control is its only safe reading.
function normalizeBucket(bucket: Bucket | 'holdout'): Bucket {
  return bucket === 'holdout' ? 'control' : bucket;
}

// The real key outranks the smoke key; a key drives dispatch when it is
// active OR its abTest() resolved off-control (a ?ab_test[key]=arm override
// or a prior-bucketing cookie). With both keys inactive and no override —
// the as-merged state until activation — this returns null and the harness
// is a pure passthrough. Calling abTest() here is the wave's sole membership
// recording trigger (records only while that key is active; sessionStorage
// dedupes repeats).
function resolveBucket(): ResolvedTest | null {
  const waveActive: boolean = WAVE_TEST_CONFIG.active;
  const waveBucket = normalizeBucket(waveAbTest());
  if (waveActive || waveBucket !== 'control') {
    return { testName: WAVE_TEST_NAME, active: waveActive, bucket: waveBucket };
  }

  const smokeActive: boolean = SMOKE_TEST_CONFIG.active;
  const smokeBucket = normalizeBucket(smokeAbTest());
  if (smokeActive || smokeBucket !== 'control') {
    return {
      testName: SMOKE_TEST_NAME,
      active: smokeActive,
      bucket: smokeBucket,
    };
  }

  return null;
}

export function resolveLoginModalAutotest(
  detail: LoginModalEventDetail,
): LoginModalAutotestResolution {
  try {
    const resolved = resolveBucket();
    if (!resolved) {
      return { detail };
    }

    const baseStamp: AutotestStamp = {
      ...(resolved.active && { autotest: resolved.testName }),
      autotest_bucket: resolved.bucket,
      modal_version:
        detail.headerVariant === 'yourPaperCard' ? 'paper_card' : 'default',
    };

    if (resolved.bucket === 'control') {
      // Control MUST see the original detail object, untouched — the codified
      // Q2 v1 lesson. Enforced by the identity test in harness.test.ts.
      return { detail };
    }

    // Smoke buckets never reach the ARMS table: the smoke key keeps its
    // launch-time empty-registry behavior (stamps, control render) even after
    // the arm PRs fill the registry, because smoke stays active in production
    // until real-key activation.
    const spec =
      resolved.testName === WAVE_TEST_NAME ? ARMS[resolved.bucket] : undefined;
    return armResolution(detail, spec, baseStamp);
  } catch {
    // Any harness failure degrades to exact control behavior.
    return { detail };
  }
}

function armResolution(
  detail: LoginModalEventDetail,
  spec: ArmSpec | undefined,
  baseStamp: AutotestStamp,
): LoginModalAutotestResolution {
  const ctx: OpenContext = { detail };
  const stamp: AutotestStamp = { ...baseStamp, ...resolveArmStamp(spec, ctx) };
  const resolution: LoginModalAutotestResolution = {
    detail: {
      ...detail,
      configuredText: mergeConfiguredText(
        detail.configuredText,
        spec?.configuredTextOverride?.(ctx),
      ),
      clickTrackingContext: stampTrackingContext(
        detail.clickTrackingContext,
        stamp,
      ),
    },
    autotest: buildHooks(spec, ctx),
  };
  // After every read path above, so they all observe pre-open state.
  spec?.onOpen?.(ctx);
  return resolution;
}

function resolveArmStamp(
  spec: ArmSpec | undefined,
  ctx: OpenContext,
): ReturnType<NonNullable<ArmSpec['stamp']>> {
  try {
    return spec?.stamp?.(ctx);
  } catch {
    return undefined;
  }
}

function buildHooks(
  spec: ArmSpec | undefined,
  ctx: OpenContext,
): LoginModalAutotestHooks | undefined {
  if (!spec) return undefined;

  const slots = spec.slots?.(ctx);
  const hooks: LoginModalAutotestHooks = {
    ...(slots && { slots }),
    ...(spec.wrapOnSuccess && { wrapOnSuccess: spec.wrapOnSuccess }),
    ...(spec.onClose && { onClose: () => spec.onClose?.(ctx) }),
    ...(spec.ctaDefaultExpanded && {
      ctaDefaultExpanded: spec.ctaDefaultExpanded(ctx),
    }),
  };
  return Object.keys(hooks).length > 0 ? hooks : undefined;
}

// Per-page shallow merge, arm override winning — the same merge shape
// LoginModal applies over its defaults, so empty-string overrides (quiet
// mode) blank slots exactly like production configuredText senders do.
function mergeConfiguredText(
  base: PartialConfigurableText | undefined,
  override: PartialConfigurableText | undefined,
): PartialConfigurableText | undefined {
  if (!override) return base;
  if (!base) return override;
  return {
    buttonsPage: { ...base.buttonsPage, ...override.buttonsPage },
    emailEntryPage: { ...base.emailEntryPage, ...override.emailEntryPage },
    emailRegistrationPage: {
      ...base.emailRegistrationPage,
      ...override.emailRegistrationPage,
    },
    emailLoginPage: { ...base.emailLoginPage, ...override.emailLoginPage },
  };
}

// Runs once on every page where the manager mounts, for all visitors while
// the wave key is live. Storage and dock UI only: never records A/B
// membership (abTest is side-effect free while the key is inactive, and the
// gate short-circuits before calling it while active) and never fires
// telemetry. getTrailSeed skips the full work-context scrape AND its
// permanent memoization, so open-time getWorkContext readers never inherit a
// page-load-time snapshot.
export function useAutotestPageInit(): void {
  useEffect(() => {
    if (!pageInitApplies()) return;

    try {
      const seed = getTrailSeed();
      if (seed) {
        pushTrailEntry(seed);
      }
    } catch {
      /* trail push is best-effort */
    }

    try {
      rehydrateDockPill();
    } catch {
      /* dock rehydration is best-effort */
    }
  }, []);
}

// Mirrors resolveBucket's wave half: page-level state only matters when the
// wave key could dispatch an arm this session (active, or forced off-control
// by a URL override / prior-bucket cookie).
function pageInitApplies(): boolean {
  try {
    return (
      WAVE_TEST_CONFIG.active || normalizeBucket(waveAbTest()) !== 'control'
    );
  } catch {
    return false;
  }
}
